<template>
  <div v-if="isTooltipInDom" v-show="isShowed" ref="rootEl" class="base-tooltip">
    <!-- v-if="isTooltipInDom" - монтируем в DOM вручную -->
    <div
      v-if="interactive"
      :style="{
        top: -interactiveOffset + 'px',
        left: -interactiveOffset + 'px',
        width: 'calc(100% + ' + interactiveOffset * 2 + 'px)',
        height: 'calc(100% + ' + interactiveOffset * 2 + 'px)',
      }"
      class="base-tooltip__interactive"
    ></div>

    <!-- eslint-disable vue/no-v-html -->
    <div v-if="forDelegateContent" v-html="forDelegateContent"></div>
    <slot v-else></slot>

    <div v-if="arrow" ref="arrow" class="base-tooltip__arrow"></div>
  </div>
</template>

<script>
/**
 * Универсальный базовый компонент всплывающих подсказок
 * Работает в любых проектах vue2, vue3, js, ts, pure css
 */

/** @import {PropType} from "vue" */
/** @import {Placement, Strategy, Middleware, OffsetOptions, ShiftOptions, HideOptions} from "@floating-ui/dom" */

import { computePosition, autoUpdate, flip, shift, offset, arrow, hide } from "@floating-ui/dom";
import { ResizeObserver as ResizeObserverPolyfill } from "@juggle/resize-observer"; // polyfill ResizeObserver

if (!window.ResizeObserver) {
  window.ResizeObserver = ResizeObserverPolyfill; // для 1С подключаем polyfill если надо (нужен для autoUpdate)
}

export default {
  props: {
    for: {
      type: [String, Number],
      default: "",
    },

    /**
     * Если очень много простых тултипов (например, подсказки при ховере) - несколько тысяч,
     * то есть вероятность, что будет потребляться много памяти и процессора.
     * Производительнее будет создать один BaseTooltip, а ховер слушать у обшего родителя.
     * В свойстве for-delegate передается уникальный css-селектор элементов, над которыми должны всплывать подсказки.
     * Например for-delegate="[tooltip-ref^=report-icon-]", а у элемента должно быть свойство :tooltip-ref="'report-icon-' + index".
     */
    forDelegate: {
      type: String,
      default: "",
    },

    /**
     * Из какого атрибута у элемента (над которым должна всплывать подсказка) будет браться контент.
     * Контент должен быть простым - строка. Вложенные компоненты, интерактивность (как в слотах) - не поддерживается.
     */
    forContentIn: {
      type: String,
      default: "tooltip-content",
    },

    /**
     * Какому элементу делегируем слушать события. Внутри него ищутся элементы, над которыми должны всплывать подсказки.
     */
    forDelegateRoot: {
      type: String,
      default: "body",
    },

    trigger: {
      type: String,
      default: "hover",
      /** @param {string} value */
      validator(value) {
        return ["hover", "click", "rclick", "manual"].includes(value);
      },
    },

    placement: {
      type: /** @type {PropType<Placement>} */ (String),
      default: "top",
    },

    /**
     * Для ускорения работы в тонком клиенте
     * Явно указать запасные положения или false (поведение по-умолчанию: противоположное положение)
     */
    fallbackPlacements: {
      type: /** @type {PropType<Placement[]|false>} */ ([Array, Boolean]),
      default: false,
    },

    appendTo: {
      type: String,
      default: "",
    },

    arrow: {
      type: /** @type {PropType<boolean>} */ (Boolean),
      default: true,
    },

    /**
     * Стратегия позиционирования ("absolute"|"fixed")
     * Стратегия "fixed" в некоторых случаях при hover сильно тормозит в тонком клиенте. В таких случаях, по возможности, надо использовать append-to="body"
     */
    strategy: {
      type: /** @type {PropType<Strategy>} */ (String),
      default: "absolute",
    },

    hideOnClick: {
      type: [Boolean, String],
      default: true,
      /** @param {string} value */
      validator(value) {
        return [
          false, // не скрывать тултип
          true, // скрывать тултип при клике по элементу или любому другому элементу
          "target", // скрывать тултип при клике по элементу
          "outside", // скрывать тултип при клике только по другим элементам
        ].includes(value);
      },
    },

    /**
     * Возможность взаимодействовать с тултипом (переходить на него)
     */
    interactive: {
      type: Boolean,
      default: false,
    },

    beforeClose: {
      type: Function,
      default: () => {
        return true;
      },
    },

    /**
     * Время в мс - задержка перед показом тултипа
     */
    delay: {
      type: Number,
      default: 0,
    },

    /**
     * Время в мс - после истечения будет закрыт тултип
     */
    timeout: {
      type: Number,
      default: 0,
    },

    /**
     * Отступ тултипа от элемента
     */
    offset: {
      type: /** @type {PropType<OffsetOptions|false>} */ ([Number, Object, Function, Boolean]),
      default: 8,
    },

    /**
     * Сдвигает тултип относительно элемента, что тултип был полностью показан во вьюпорте.
     * padding - минимальное растояние до краев ограничивающего элемента, экрана.
     * Для ускорения работы в тонком клиенте, false - отключает
     */
    shift: {
      type: /** @type {PropType<ShiftOptions|false>} */ ([Object, Boolean]),
      default: () => ({ padding: 5 }),
    },

    /**
     * Скрывает тултип, если элемент скрывается с экрана
     * Для ускорения работы в тонком клиенте, false - отключает
     */
    hidding: {
      type: /** @type {PropType<HideOptions|false>} */ ([Object, Boolean]),
      default: () => ({}),
    },

    /**
     * Автообновление положения тултипа
     * Для ускорения работы в тонком клиенте, false - отключает
     */
    autoUpdate: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isTooltipInDom: false, // сразу не монтируем - для производительности
      isShowed: false,
      mouseOverTargetOnHover: false,
      forDelegateContent: "",

      /** @type {HTMLElement&SVGElement|null} */
      target: null,

      /** @type {HTMLElement|null} */
      tooltipElement: null,

      /** @type {HTMLElement&SVGElement|null} */
      tooltipCaret: null,

      /** @type {Function|null} */
      stopAutoUpdate: null,

      /** @type {number|null} */
      clientX: null,

      /** @type {number|null} */
      clientY: null,
    };
  },

  computed: {
    interactiveOffset() {
      return typeof this.offset === "number" ? this.offset : 0;
    },
  },

  async mounted() {
    // для производительности начинать работу в mounted, а не в created
    await this.$nextTick(); // если будет вложен глубоко для querySelector

    // если делегировали событие показа тултипа
    if (this.forDelegate) {
      if (this.trigger === "hover") {
        /** @type {HTMLElement|null} */
        const forDelegateRoot = document.querySelector(this.forDelegateRoot);

        if (forDelegateRoot) {
          forDelegateRoot.addEventListener("mouseover", this.forDelegateRootMouseover);
          forDelegateRoot.addEventListener("mouseout", this.forDelegateRootMouseout);
        }
      }
    }

    // если тултип привязали к конкретному элементу
    if (this.for) {
      // для производительности - поиск в DOM в конце
      // @ts-expect-error - TODO: может вернуть vnode
      this.target =
        this.$parent?.$refs[this.for] ||
        this.$parent?.$parent?.$refs[this.for] ||
        this.$parent?.$parent?.$parent?.$refs[this.for] ||
        this.$parent?.$parent?.$parent?.$parent?.$refs[this.for] ||
        this.$parent?.$parent?.$parent?.$parent?.$parent?.$refs[this.for] ||
        document.querySelector('[tooltip-name="' + this.for + '"]') ||
        document.querySelector(String(this.for));

      if (!this.target) {
        return;
      }

      // @ts-expect-error - TODO: this.target может вернуть vnode, надо определить $el
      this.target = this.target?.$el || this.target;

      if (Array.isArray(this.target)) {
        this.target = this.target[0];
      }

      // @ts-expect-error - TODO: this.target может вернуть vnode, надо определить $el
      this.target = this.target?.$el || this.target; // HACK: еще раз

      if (!this.target) {
        return;
      }

      if (this.trigger !== "manual") {
        if (this.trigger === "hover") {
          this.target.addEventListener("mouseenter", this.targetMouseenter);
          this.target.addEventListener("mouseleave", this.targetMouseleave);
        }

        if (this.trigger === "rclick") {
          this.target.addEventListener("contextmenu", this.targetContextmenu);
        }

        document.body.addEventListener("mousedown", this.toggleBodyClick); // слушаем клики всегда, даже при hover (т.к. может быть [hide-on-select])
      }
    }

    this.$emit("init", this);
  },

  beforeDestroy() {
    document.body.removeEventListener("mousedown", this.toggleBodyClick);

    if (this.target) {
      if (this.trigger === "hover") {
        this.target.removeEventListener("mouseenter", this.targetMouseenter);
        this.target.removeEventListener("mouseleave", this.targetMouseleave);

        if (this.interactive && this.tooltipElement) {
          this.tooltipElement.removeEventListener("mouseleave", this.tooltipMouseleave);
        }
      }

      if (this.trigger === "rclick") {
        this.target.removeEventListener("contextmenu", this.targetContextmenu);
      }
    }

    if (this.forDelegate) {
      if (this.trigger === "hover") {
        /** @type {HTMLElement|null} */
        const forDelegateRoot = document.querySelector(this.forDelegateRoot);

        if (forDelegateRoot) {
          forDelegateRoot.removeEventListener("mouseover", this.forDelegateRootMouseover);
          forDelegateRoot.removeEventListener("mouseout", this.forDelegateRootMouseout);
        }
      }
    }

    if (this.stopAutoUpdate) {
      this.stopAutoUpdate();
    }

    if (this.appendTo && this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  },

  methods: {
    async addTooltipToDom() {
      this.isTooltipInDom = true;

      await this.$nextTick();

      if (!(this.$refs.rootEl instanceof HTMLElement)) {
        return;
      }

      this.tooltipElement = this.$refs.rootEl;

      if (this.appendTo) {
        const appendToEl = document.querySelector(this.appendTo);

        if (appendToEl && this.tooltipElement) {
          appendToEl.append(this.tooltipElement);
        }
      }

      // ищем caret тут, а не в mounted, т.к. querySelector может быть долгим
      this.tooltipCaret = this.target?.querySelector("[tooltip-caret]") || null;

      if (this.tooltipCaret) {
        this.tooltipCaret.style.transition = ".2s";
      }

      if (this.trigger === "hover" && this.interactive) {
        this.tooltipElement?.addEventListener("mouseleave", this.tooltipMouseleave);
      }
    },

    async update() {
      if (!this.target || !this.tooltipElement?.style) {
        return;
      }

      const options = {};
      let target = this.target;

      this.tooltipElement.style.position = this.strategy;
      options.strategy = this.strategy;
      options.placement = this.placement;
      options.middleware = /** @type {Middleware[]} */ ([]);

      // should generally be placed at the beginning of your middleware array
      if (this.offset) {
        options.middleware.push(offset(this.offset));
      }

      if (this.fallbackPlacements && this.fallbackPlacements.length) {
        options.middleware.push(flip({ fallbackPlacements: this.fallbackPlacements }));
      } else {
        options.middleware.push(flip());
      }

      if (this.shift) {
        options.middleware.push(shift(this.shift));
      }

      if (this.arrow && this.$refs.arrow instanceof HTMLElement) {
        options.middleware.push(arrow({ element: this.$refs.arrow, padding: 6 }));
      }

      // at the end of middleware
      if (this.hidding) {
        options.middleware.push(hide(this.hidding));
      }

      if (this.trigger === "rclick" && Number(this.clientX) >= 0 && Number(this.clientY) >= 0) {
        const clientX = Number(this.clientX);
        const clientY = Number(this.clientY);

        target = {
          ...this.target,

          /** @return {DOMRect} */
          getBoundingClientRect() {
            // @ts-expect-error - TODO: эмулируем getBoundingClientRect
            return {
              width: 0,
              height: 0,
              x: clientX,
              y: clientY,
              top: clientY,
              left: clientX,
              right: clientX,
              bottom: clientY,
            };
          },
        };
      }

      const { x, y, placement, middlewareData } = await computePosition(target, this.tooltipElement, options);

      Object.assign(this.tooltipElement.style, {
        top: "0",
        left: "0",
        transform: "translate(" + Math.round(x) + "px, " + Math.round(y) + "px)",
        visibility: middlewareData.hide?.referenceHidden ? "hidden" : "visible",
      });

      if (this.arrow && this.$refs.arrow instanceof HTMLElement) {
        const backgroundColor = getComputedStyle(this.tooltipElement).backgroundColor;
        // @ts-expect-error - TODO: почему-то не видит в типах x и y
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        /** @type {string} */
        const mainStaticSide =
          {
            top: "bottom",
            right: "left",
            bottom: "top",
            left: "right",
          }[placement.split("-")[0]] || "";

        /** @type {string} */
        const crossStaticSide =
          {
            top: "",
            right: "",
            bottom: "",
            left: "",
            "right-start": "top",
            "left-start": "top",
            "right-end": "bottom",
            "left-end": "bottom",
            "top-start": "left",
            "bottom-start": "left",
            "top-end": "right",
            "bottom-end": "right",
          }[placement] || "";

        Object.assign(this.$refs.arrow.style, {
          left: arrowX !== null && mainStaticSide !== "right" && !crossStaticSide ? arrowX + "px" : "",
          top: arrowY !== null && mainStaticSide !== "bottom" && !crossStaticSide ? arrowY + "px" : "",
          right: "",
          bottom: "",
          [mainStaticSide]: "-4px",
          [crossStaticSide]: "10px",
          backgroundColor: backgroundColor === "rgba(0, 0, 0, 0)" ? "#fff" : backgroundColor,
        });
      }
    },

    targetMouseenter() {
      this.mouseOverTargetOnHover = true;

      if (!this.isShowed) {
        this.show();
      }
    },

    /**
     * @param {MouseEvent} e
     */
    targetMouseleave(e) {
      this.mouseOverTargetOnHover = false;

      if (this.isShowed) {
        if (this.interactive) {
          if (!(e.relatedTarget instanceof HTMLElement) && !(e.relatedTarget instanceof SVGElement)) {
            return;
          }

          const leaveToTooltip = this.tooltipElement?.contains(e.relatedTarget);

          if (!leaveToTooltip) {
            this.hide();
          }
        } else {
          this.hide();
        }
      }
    },

    /**
     * @param {MouseEvent} e
     */
    forDelegateRootMouseover(e) {
      if (!e.target || (!(e.target instanceof HTMLElement) && !(e.target instanceof SVGElement))) {
        return;
      }

      /** @type {HTMLElement&SVGElement|null} */
      const target = e.target?.closest(this.forDelegate);

      if (target) {
        this.target = target;

        if (!this.isShowed) {
          this.forDelegateContent = this.target?.getAttribute(this.forContentIn) || "";
          this.show();
        }
      }
    },

    forDelegateRootMouseout() {
      if (this.isShowed) {
        this.hide();
      }

      this.target = null;
      this.forDelegateContent = "";
    },

    /**
     * @param {MouseEvent} e
     */
    tooltipMouseleave(e) {
      if (this.isShowed) {
        if (this.interactive) {
          if (!(e.relatedTarget instanceof HTMLElement) && !(e.relatedTarget instanceof SVGElement)) {
            return;
          }

          const leaveToTarget = this.target?.contains(e.relatedTarget);

          if (!leaveToTarget) {
            this.hide();
          }
        } else {
          this.hide();
        }
      }
    },

    /**
     * @param {MouseEvent} e
     */
    targetContextmenu(e) {
      e.preventDefault();
    },

    /**
     * Для удобства слушаем все клики на document.body в одном месте (а не на this.target отдельно)
     * @param {MouseEvent} e
     */
    toggleBodyClick(e) {
      // Не делать preventDefault, т.к. слушаем клик на body

      // только левая или правая кнопка мыши или dispathEvent
      if (e.button !== 0 && e.button !== 2 && e.isTrusted) {
        return;
      }

      if (!e.target || !(e.target instanceof HTMLElement || e.target instanceof SVGElement)) {
        return;
      }

      const clickForThisTooltip = e.target.closest(".base-tooltip") === this.tooltipElement; // в случае вложенных тултипов
      const clickOnTarget = this.target?.contains(e.target);
      const clickOnTooltip = this.tooltipElement?.contains(e.target);
      const hideOnSelectEl = e.target.closest("[hide-on-select]");
      const clickOnHideOnSelect =
        clickForThisTooltip && hideOnSelectEl && this.tooltipElement?.contains(hideOnSelectEl);

      if (this.isShowed) {
        if (this.hideOnClick) {
          if (clickOnTarget && !clickForThisTooltip) {
            if (this.hideOnClick === true || this.hideOnClick === "target") {
              this.hide();
            }
          } else if (clickOnTooltip) {
            if (clickOnHideOnSelect) {
              // HACK: для [hide-on-select] слушаем именно клик, поэтому на mousedown - ждем ответный mouseup
              const _this = this;
              document.body.addEventListener("mouseup", function temporary() {
                _this.hide();
                document.body.removeEventListener("mouseup", temporary);
              });
            } else {
              return;
            }
          } else {
            if (this.hideOnClick === true || this.hideOnClick === "outside") {
              this.hide();
            }
          }
        }
      } else {
        if (clickOnTarget) {
          if (this.trigger === "click" && e.button === 0) {
            this.show();
          } else if (this.trigger === "rclick" && e.button === 2) {
            // считаем что правый клик всегда для вызова контекстного меню
            this.clientX = e.clientX;
            this.clientY = e.clientY;
            this.show();
          }
        }
      }
    },

    async show() {
      if (!this.isTooltipInDom) {
        await this.addTooltipToDom();
      }

      if (!this.target || !this.tooltipElement?.style) {
        return;
      }

      // если установили задержку показа
      if (this.delay) {
        await new Promise((resolve) =>
          setTimeout(() => {
            if (this.trigger === "hover") {
              if (this.mouseOverTargetOnHover) {
                resolve(true);
              }
            } else {
              resolve(true);
            }
          }, this.delay),
        );
      }

      // если установили таймер закрытия
      if (this.timeout) {
        setTimeout(() => {
          this.hide();
        }, this.timeout);
      }

      this.isShowed = true;

      await this.update();

      if (this.autoUpdate) {
        this.stopAutoUpdate = autoUpdate(this.target, this.tooltipElement, this.update); // нужен ResizeObserver
      }

      if (this.tooltipCaret) {
        this.tooltipCaret.classList.add("rotate-180");
      }

      this.$emit("open", this);
    },

    async hide() {
      // Если указали функцию beforeClose, то она должна вернуть true, чтобы продолжить закрытие
      const beforeCloseResult = await this.beforeClose.bind(this.$parent)();

      if (!beforeCloseResult) {
        return;
      }

      if (this.stopAutoUpdate) {
        this.stopAutoUpdate();
      }

      if (this.tooltipCaret) {
        this.tooltipCaret.classList.remove("rotate-180");
      }

      this.isShowed = false;
      this.$emit("close", this);
    },
  },
};
</script>

<style>
:root {
  --tooltip-padding: 10px 15px;
  --tooltip-border-radius: var(--border-radius, 6px);
  --tooltip-z-index: 10000;
}
</style>

<style scoped>
.base-tooltip {
  position: relative;
  z-index: var(--tooltip-z-index);
  padding: var(--tooltip-padding);
  border-radius: var(--tooltip-border-radius);
  background-color: #fff;
  box-shadow: 0 0 12px 3px rgba(0, 0, 0, 0.1);
}

.base-tooltip__arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  transform: rotate(45deg);
}

.base-tooltip__interactive {
  position: absolute;
  z-index: -1;
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>
